import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Compass,
  Moon,
  Sun,
  HelpCircle,
  BookOpen,
  List,
  History,
  Zap,
  FlaskConical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { GitHubAuthButton } from '@/components/auth/GitHubAuthButton';
import { UserMenu } from '@/components/auth/UserMenu';
import { Separator } from '@/components/ui/separator';
import { MeshpitLogo } from '@/components/MeshpitLogo';
import { HelpDialog } from '@/components/help/HelpDialog';
import { useState } from 'react';

export function Menubar() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const [showHelp, setShowHelp] = useState(false);

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <>
      <div className="flex h-11 items-center justify-between border-b border-border bg-background px-3">
        {/* Left group: Logo + Navigation */}
        <div className="flex items-center gap-1">
          {/* Logo */}
          <Link to="/" className="mr-2 flex items-center">
            <MeshpitLogo className="h-6 w-6 text-foreground" />
          </Link>

          <Separator orientation="vertical" className="mx-1 h-5" />

          {/* New Model */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 px-2"
                onClick={() => navigate('/new')}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>New Model</TooltipContent>
          </Tooltip>

          {/* Explore */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 px-2"
                asChild
              >
                <Link to="/explore">
                  <Compass className="h-4 w-4" />
                  <span className="hidden sm:inline">Explore</span>
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Explore Models</TooltipContent>
          </Tooltip>
        </div>

        {/* Right group: Theme + GitHub + Auth */}
        <div className="flex items-center gap-1">
          {/* Help / Docs dropdown */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>Help & Docs</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Documentation
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link
                  to="/features"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Zap className="h-3.5 w-3.5" />
                  Features
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/examples"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <FlaskConical className="h-3.5 w-3.5" />
                  Examples
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/cheatsheet"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <List className="h-3.5 w-3.5" />
                  Parameter Cheatsheet
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  to="/blog"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  Blog
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link
                  to="/changelog"
                  className="flex cursor-pointer items-center gap-2"
                >
                  <History className="h-3.5 w-3.5" />
                  Changelog
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowHelp(true)}
                className="cursor-pointer"
              >
                <HelpCircle className="mr-2 h-3.5 w-3.5" />
                Help &amp; Keyboard Shortcuts
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* GitHub */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a
                  href="https://github.com/Meshpit/Meshpit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    className="h-4 w-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                  </svg>
                </a>
              </Button>
            </TooltipTrigger>
            <TooltipContent>GitHub</TooltipContent>
          </Tooltip>

          {/* Theme Toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={toggleTheme}
              >
                {resolvedTheme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
            </TooltipContent>
          </Tooltip>

          <Separator orientation="vertical" className="mx-1 h-5" />

          {/* Auth */}
          {isLoading ? (
            <div className="h-7 w-7 animate-pulse rounded-full bg-muted" />
          ) : isAuthenticated ? (
            <UserMenu collapsed />
          ) : (
            <GitHubAuthButton
              variant="ghost"
              size="sm"
              showText={false}
              className="h-8 w-8 p-0"
            />
          )}
        </div>
      </div>

      <HelpDialog open={showHelp} onOpenChange={setShowHelp} />
    </>
  );
}
